"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSchool } from "@/lib/contexts/SchoolContext";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { toastError } from '@/lib/utils';
import { pluralize } from "@/lib/utils";

interface SubData {
  subscriptions: {
    id: string;
    product_id: string;
    status: string;
    max_schools: number;
    stripe_subscription_id: string | null;
    interval: string | null;
    linked_schools?: { id: string }[];
    trial_ends_at?: string | null;
  }[];
}

interface QuickLinkDialogProps {
  addHubTarget: { id: string; name: string; href: string } | null;
  setAddHubTarget: (target: { id: string; name: string; href: string } | null) => void;
  subData: SubData | undefined;
}

export function QuickLinkDialog({ addHubTarget, setAddHubTarget, subData }: QuickLinkDialogProps) {
  const router = useRouter();
  const { session } = useAuth();
  const { activeSchool } = useSchool();
  const queryClient = useQueryClient();
  const [addHubLoading, setAddHubLoading] = useState(false);

  return (
    <AlertDialog open={!!addHubTarget} onOpenChange={(open) => { if (!open) setAddHubTarget(null) }}>
      <AlertDialogContent className="bg-white">
        {(() => {
          if (!addHubTarget) return null;
          const subs = subData?.subscriptions || [];
          const hubSub = subs.find(s => s.product_id === addHubTarget.id && ['active', 'trialing', 'cancelling'].includes(s.status));
          const linkedCount = hubSub?.linked_schools?.length ?? 0;
          const maxSchools = hubSub?.max_schools ?? 3;
          const schoolName = activeSchool?.name || 'your school';

          return (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Link {schoolName} to {addHubTarget.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  You&apos;re using {linkedCount} of {maxSchools} {pluralize(maxSchools, 'school')} in your {addHubTarget.name} subscription. Would you like to link {schoolName}? You&apos;ll then be using {linkedCount + 1} of {maxSchools}.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={addHubLoading}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={addHubLoading}
                  onClick={async (e) => {
                    e.preventDefault();
                    if (!hubSub || !activeSchool || !session?.access_token) return;
                    setAddHubLoading(true);
                    try {
                      const res = await fetch('/api/subscriptions/link-school', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                        body: JSON.stringify({ subscriptionId: hubSub.id, schoolId: activeSchool.id }),
                      });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.error || 'Failed to link school');
                      queryClient.invalidateQueries({ queryKey: ['userSubscriptions'] });
                      queryClient.invalidateQueries({ queryKey: ['schools'] });
                      queryClient.invalidateQueries({ queryKey: ['productAccess'] });
                      queryClient.invalidateQueries({ queryKey: ['unusedSeats'] });
                      toast.success(`${schoolName} linked to ${addHubTarget.name}!`);
                      const href = addHubTarget.href;
                      setAddHubTarget(null);
                      router.push(href);
                    } catch (err) {
                      toastError(err, 'Failed to link school');
                    } finally {
                      setAddHubLoading(false);
                    }
                  }}
                >
                  {addHubLoading ? 'Linking...' : 'Link School'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          );
        })()}
      </AlertDialogContent>
    </AlertDialog>
  );
}
